import { useMemo, useState } from 'react'
import { X, Split, Plus, Trash2, Check, AlertTriangle } from 'lucide-react'

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const r2 = (v) => Math.round((Number(v) || 0) * 100) / 100

const CRITERIOS = [
  { id: 'tipo',    label: 'Tipo de animal' },
  { id: 'sexo',    label: 'Sexo' },
  { id: 'fazenda', label: 'Fazenda do lote' },
]

let _gid = 0
const novoGrupo = (valores = [], nome = '') => ({ id: ++_gid, nome, valores, desp: null })

/**
 * Modal para separar a fatura do vendedor em várias faturas (só apresentação).
 *
 * Props:
 * - opcoes: { tipo|sexo|fazenda: [{ valor, lotes, animais, valor_total, despesas }] }
 * - fluxo: fluxo_comissao do leilão (despesas só são redistribuíveis em separada/cavalo)
 * - atual: configuração aplicada hoje ({ criterio, grupos }) ou null
 * - onApply: (cfg | null) => void   (null = voltar à fatura única)
 * - onClose: () => void
 */
export default function SepararFaturaModal({ opcoes, fluxo, atual, onApply, onClose, modoLista = false, criterios = CRITERIOS, entidade = 'vendedor', semDespesa = false }) {
  const item = modoLista ? 'lista' : 'fatura'
  const [criterio, setCriterio] = useState(atual?.criterio || 'tipo')
  const [grupos, setGrupos] = useState(() =>
    atual?.grupos?.length
      ? atual.grupos.filter(g => g.nome !== 'Demais').map(g => ({ ...novoGrupo(g.valores, g.nome), desp: g.desp ?? null }))
      : [])
  const [ativo, setAtivo] = useState(null)

  const podeDespesa = !modoLista && !semDespesa && ['separada', 'cavalo'].includes(fluxo)
  const lista = opcoes?.[criterio] || []
  const porValor = useMemo(() => Object.fromEntries(lista.map(o => [o.valor, o])), [lista])

  const usados = new Set(grupos.flatMap(g => g.valores))
  const livres = lista.filter(o => !usados.has(o.valor))

  const trocarCriterio = (c) => { setCriterio(c); setGrupos([]); setAtivo(null) }

  const soma = (valores, campo) => valores.reduce((s, v) => s + (porValor[v]?.[campo] || 0), 0)
  const totalGeral = lista.reduce((s, o) => s + o.valor_total, 0)
  const lista_total_animais = lista.reduce((s, o) => s + o.animais, 0)
  const totalDesp = r2(lista.reduce((s, o) => s + o.despesas, 0))

  // Grupos efetivos = os criados + "Demais" (sobra), que é materializado ao aplicar
  const efetivos = [
    ...grupos,
    ...(livres.length && grupos.length ? [{ id: 'demais', nome: 'Demais', valores: livres.map(o => o.valor), desp: null, auto: true }] : []),
  ]
  const editouDesp = podeDespesa && grupos.some(g => g.desp !== null)
  const despGrupo = (g) => {
    if (g.auto && editouDesp) {
      // "Demais" absorve a diferença para o total de despesas fechar
      return r2(totalDesp - grupos.reduce((s, x) => s + (x.desp ?? r2(soma(x.valores, 'despesas'))), 0))
    }
    return g.desp ?? r2(soma(g.valores, 'despesas'))
  }
  const somaDesp = r2(efetivos.reduce((s, g) => s + despGrupo(g), 0))
  const difDesp = r2(totalDesp - somaDesp)
  const despOk = !editouDesp || (Math.abs(difDesp) < 0.005 && efetivos.every(g => despGrupo(g) >= 0))

  const umaPorValor = () => {
    setGrupos(lista.map(o => novoGrupo([o.valor], o.valor.charAt(0) + o.valor.slice(1).toLowerCase())))
    setAtivo(null)
  }
  const addGrupo = () => { const g = novoGrupo(); setGrupos(gs => [...gs, g]); setAtivo(g.id) }
  const delGrupo = (id) => { setGrupos(gs => gs.filter(g => g.id !== id)); if (ativo === id) setAtivo(null) }
  const setGrupo = (id, patch) => setGrupos(gs => gs.map(g => g.id === id ? { ...g, ...patch } : g))

  const clicarLivre = (valor) => {
    let alvo = ativo
    if (!alvo) {
      const g = novoGrupo(); setGrupos(gs => [...gs, g]); setAtivo(g.id); alvo = g.id
      setGrupos(gs => gs.map(x => x.id === alvo ? { ...x, valores: [...x.valores, valor] } : x))
      return
    }
    setGrupos(gs => gs.map(g => g.id === alvo ? { ...g, valores: [...g.valores, valor] } : g))
  }
  const removerChip = (gid, valor) => setGrupo(gid, { valores: grupos.find(g => g.id === gid).valores.filter(v => v !== valor) })

  const montarConfig = () => ({
    criterio,
    grupos: efetivos.filter(g => g.valores.length).map(g => ({
      nome: g.nome,
      valores: g.valores,
      desp: editouDesp ? despGrupo(g) : null,
    })),
  })

  const nGrupos = efetivos.filter(g => g.valores.length).length
  const podeAplicar = nGrupos >= 2 && despOk

  const Chip = ({ o, onClick, ativoChip, titulo }) => (
    <button type="button" onClick={onClick} title={titulo}
      className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs transition-colors text-left ${ativoChip
        ? 'bg-blue-50 border-blue-300 text-blue-800 hover:bg-red-50 hover:border-red-300 hover:text-red-700'
        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400 hover:bg-blue-50'}`}>
      <span className="font-semibold">{o.valor}</span>
      <span className="text-gray-500">{o.animais} anim. · {o.lotes} lote{o.lotes > 1 ? 's' : ''}</span>
    </button>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-4xl rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col" style={{ maxHeight: '92dvh' }}>
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-gray-200">
          <Split className="w-5 h-5 text-blue-600" />
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-800 text-base">{modoLista ? `Separar lista do ${entidade}` : `Separar fatura do ${entidade}`}</h2>
            <p className="text-xs text-gray-500">{modoLista ? 'Apenas a apresentação e a impressão mudam.' : (entidade === 'comprador' ? 'Apenas a apresentação e a impressão mudam. Os valores e as comissões não são alterados.' : 'Apenas a apresentação e a impressão mudam. Comprador, pagamentos e comissões não são afetados.')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Critério */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1.5">Separar por</div>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              {criterios.map(c => (
                <button key={c.id} onClick={() => trocarCriterio(c.id)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${criterio === c.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {lista.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">Nenhum dado disponível para este critério.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Disponíveis */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-gray-500">Disponíveis ({livres.length})</span>
                  <button onClick={umaPorValor} className="text-xs text-blue-600 hover:underline">{modoLista ? 'Uma lista para cada' : 'Uma fatura para cada'}</button>
                </div>
                <div className="flex flex-wrap md:flex-col gap-1.5 p-2 bg-gray-50 border border-gray-200 rounded-lg min-h-[60px] md:max-h-72 md:overflow-y-auto">
                  {livres.length === 0
                    ? <span className="text-xs text-gray-400 p-1">Tudo distribuído.</span>
                    : livres.map(o => <Chip key={o.valor} o={o} onClick={() => clicarLivre(o.valor)} titulo="Adicionar à fatura selecionada" />)}
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5">Selecione uma {item} ao lado e clique nos itens para adicioná-los. O que sobrar vai para “Demais”.</p>
              </div>

              {/* Faturas */}
              <div className="md:col-span-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">{modoLista ? 'Listas' : 'Faturas'} ({nGrupos})</span>
                  <button onClick={addGrupo} className="flex items-center gap-1 text-xs text-blue-600 hover:underline"><Plus className="w-3.5 h-3.5" />{modoLista ? 'Nova lista' : 'Nova fatura'}</button>
                </div>
                {efetivos.length === 0 && (
                  <div className="text-xs text-gray-400 border border-dashed border-gray-300 rounded-lg p-4 text-center">
                    Clique em “Nova {item}” ou em um item à esquerda para começar.
                  </div>
                )}
                {efetivos.map((g, idx) => (
                  <div key={g.id} onClick={() => !g.auto && setAtivo(g.id)}
                    className={`rounded-lg border p-3 space-y-2 transition-colors ${g.auto ? 'bg-gray-50 border-gray-200' : ativo === g.id ? 'border-blue-500 ring-1 ring-blue-300 bg-white cursor-pointer' : 'border-gray-200 bg-white cursor-pointer hover:border-gray-300'}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-400 w-5">{idx + 1}</span>
                      {g.auto
                        ? <span className="flex-1 text-sm font-medium text-gray-600">Demais <span className="text-xs font-normal text-gray-400">(automático)</span></span>
                        : <input value={g.nome} onChange={e => setGrupo(g.id, { nome: e.target.value })} onClick={e => e.stopPropagation()}
                            placeholder={`Nome da ${item} (opcional)`} maxLength={40}
                            className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />}
                      <span className="text-xs text-gray-500 whitespace-nowrap">{soma(g.valores, 'animais')} anim.{modoLista ? '' : ` · ${fmt(soma(g.valores, 'valor_total'))}`}</span>
                      {!g.auto && <button onClick={e => { e.stopPropagation(); delGrupo(g.id) }} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {g.valores.length === 0 && <span className="text-xs text-gray-400">Vazia — clique nos itens à esquerda.</span>}
                      {g.valores.map(v => porValor[v] && (
                        <Chip key={v} o={porValor[v]} ativoChip titulo={g.auto ? '' : 'Remover desta fatura'}
                          onClick={e => { e?.stopPropagation?.(); if (!g.auto) removerChip(g.id, v) }} />
                      ))}
                    </div>
                    {podeDespesa && totalDesp > 0 && g.valores.length > 0 && (
                      <div className="flex items-center gap-2 pt-1 border-t border-gray-100" onClick={e => e.stopPropagation()}>
                        <span className="text-xs text-gray-500 flex-1">Despesas desta fatura</span>
                        <input type="number" step="0.01" min="0" value={despGrupo(g)}
                          onChange={e => {
                            const val = e.target.value === '' ? 0 : Number(e.target.value)
                            if (g.auto) return
                            setGrupo(g.id, { desp: val })
                          }}
                          readOnly={g.auto}
                          className={`w-28 border rounded-md px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500 ${g.auto ? 'bg-gray-100 border-gray-200 text-gray-500' : 'border-gray-300'}`} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Conferência */}
          {nGrupos >= 1 && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm rounded-lg bg-green-50 border border-green-200 text-green-800 px-3 py-2">
                <Check className="w-4 h-4" />
                {modoLista
                  ? <span>Soma das listas = <b>{efetivos.reduce((s, g) => s + soma(g.valores, 'animais'), 0)}</b> animais (total {lista_total_animais})</span>
                  : <span>Soma das faturas = <b>{fmt(efetivos.reduce((s, g) => s + soma(g.valores, 'valor_total'), 0))}</b> (total da venda {fmt(totalGeral)})</span>}
              </div>
              {podeDespesa && totalDesp > 0 && editouDesp && (
                <div className={`flex items-center gap-2 text-sm rounded-lg border px-3 py-2 ${despOk ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                  {despOk ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span>
                    Despesas distribuídas: <b>{fmt(somaDesp)}</b> de {fmt(totalDesp)}
                    {!despOk && <> — {difDesp > 0 ? `faltam ${fmt(difDesp)}` : `sobram ${fmt(-difDesp)}`}</>}
                  </span>
                </div>
              )}
              {!podeDespesa && !modoLista && !semDespesa && (
                <p className="text-[11px] text-gray-400">Neste fluxo as despesas já entram no líquido de cada lote e acompanham os lotes de cada fatura.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-3.5 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          {atual && (
            <button onClick={() => onApply(null)} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg">Desfazer separação</button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button disabled={!podeAplicar} onClick={() => onApply(montarConfig())}
            title={nGrupos < 2 ? `Crie ao menos 2 ${item}s` : !despOk ? 'Ajuste as despesas para fechar o total' : ''}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
            Aplicar separação
          </button>
        </div>
      </div>
    </div>
  )
}
